<div class="terms-wrapper">
  <div class="terms-container">
    <h1>RISK DISCLOSURE STATEMENT</h1>
    <p class="sub-text">
      <strong>Platform:</strong> Coinwush — Crypto Derivatives Trading Platform
    </p>
    <p class="sub-text"><strong>Version:</strong> 1.0</p>
    <p class="sub-text"><strong>Effective Date:</strong> {{ effectiveDate }}</p>

    <p class="sub-text"><strong>Last Updated:</strong> {{ lastUpdated }}</p>
    <p class="sub-text">
      <strong>Issued by:</strong> Questalious Private Limited (CIN:
      U63119UP2025PTC222413)
    </p>

    <section id="preamble" class="policy-section">
      <h2>PREAMBLE</h2>

      <p>
        This Risk Disclosure Statement ("Disclosure") is issued by Questalious
        Private Limited (CIN: U63119UP2025PTC222413), the operator of Coinwush
        (the "Platform"), a mobile and web-based application that provides
        retail users in India access to cryptocurrency derivatives trading,
        including Perpetual Futures, Options, and Margin Trading.
      </p>

      <p>
        Coinwush is NOT a Virtual Digital Asset (VDA) exchange and does NOT
        facilitate the buying, selling, or transfer of spot cryptocurrencies.
        The Platform operates exclusively as a derivatives trading platform.
        Users do not take delivery of any underlying cryptocurrency asset; all
        contracts are cash-settled or margin-settled as applicable.
      </p>

      <p>
        By downloading, installing, accessing, or using the Coinwush Platform,
        you confirm that you have read, fully understood, and unconditionally
        accepted all the risks described in this Disclosure. This Disclosure
        must be read together with our Terms of Use, Privacy Policy, and any
        other policies published on the Platform.
      </p>
    </section>

    <section id="nature-of-derivatives-trading" class="policy-section">
      <h2>1. NATURE OF DERIVATIVES TRADING</h2>

      <p>
        Cryptocurrency derivatives are financial contracts whose value is
        derived from the price of an underlying digital asset (e.g., Bitcoin,
        Ethereum). Unlike spot trading, derivatives trading on Coinwush involves
        contracts — not the actual purchase or ownership of any cryptocurrency.
        The following derivative products are currently offered:
      </p>

      <ul class="policy-bullets">
        <li>
          <strong>Perpetual Futures:</strong> Contracts with no expiry date that
          track the price of an underlying cryptocurrency. Positions are
          maintained via a funding rate mechanism.
        </li>
        <li>
          <strong>Options:</strong> Contracts that give the user the right, but
          not the obligation, to buy or sell an underlying asset at a
          predetermined price before or at expiry.
        </li>
        <li>
          <strong>Margin Trading:</strong> Trading that allows users to open
          positions larger than their actual account balance by using borrowed
          capital, amplifying both potential gains and potential losses.
        </li>
      </ul>

      <p>
        All products are highly speculative in nature and are intended only for
        users who fully understand the mechanics, risks, and consequences of
        leveraged derivatives trading.
      </p>
    </section>

    <h2>2. HIGH-RISK WARNING — LEVERAGE AND MARGIN</h2>

    <p><strong>THIS IS A CRITICAL RISK. PLEASE READ CAREFULLY.</strong></p>

    <p>
      Coinwush offers leverage on its derivative contracts. The leverage ratio
      is customized per contract based on the risk profile of the underlying
      asset and prevailing market conditions. Leverage significantly magnifies
      both potential profits and potential losses.
    </p>

    <ul>
      <li>You may lose your entire margin deposit in a single trade.</li>
      <li>
        In certain market conditions, including extreme volatility or low
        liquidity, losses may exceed your initial deposit.
      </li>
      <li>
        Margin calls may be triggered automatically. If your account balance
        falls below the required maintenance margin, your position(s) may be
        forcibly liquidated without prior notice.
      </li>
      <li>
        Liquidation may occur at a price less favorable than the liquidation
        trigger price, particularly during periods of high volatility or market
        gaps.
      </li>
      <li>
        Coinwush is not liable for any losses resulting from automatic
        liquidation of your positions.
      </li>
    </ul>

    <p>
      You should never trade with funds that you cannot afford to lose entirely.
      Derivatives trading with leverage is not suitable for most retail
      investors.
    </p>

    <h2>3. MARKET RISKS</h2>

    <h3>3.1 Price Volatility</h3>
    <p>
      The prices of cryptocurrency derivative contracts are directly or
      indirectly linked to the prices of underlying digital assets, which are
      highly volatile. Prices may fluctuate dramatically within seconds due to
      market sentiment, regulatory news, macroeconomic developments,
      technological changes, or other unpredictable factors. Users may suffer
      rapid and significant financial losses.
    </p>

    <h3>3.2 No Guaranteed Returns</h3>
    <p>
      Coinwush does not guarantee any profit, return on investment, or recovery
      of any funds. Past performance of any contract, strategy, or asset is not
      indicative of future results.
    </p>

    <h3>3.3 Liquidity Risk</h3>
    <p>
      Some derivative contracts may have low trading volumes or limited market
      depth. This can result in significant price slippage when entering or
      exiting positions, meaning trades may be executed at prices substantially
      different from the quoted price.
    </p>

    <h3>3.4 Funding Rate Risk (Perpetual Futures)</h3>
    <p>
      Perpetual futures contracts are subject to periodic funding rate payments
      between long and short position holders. Depending on market conditions,
      funding rates may be significantly positive or negative, resulting in
      ongoing costs or credits to your account that can materially impact your
      position profitability.
    </p>

    <h3>3.5 Counterparty Risk</h3>
    <p>
      Derivative trades involve a counterparty. The default, insolvency, or
      failure of a counterparty to honor their obligations may result in your
      trade being partially or wholly unfulfilled. Coinwush does not guarantee
      the performance of any counterparty.
    </p>

    <h3>3.6 Index and Mark Price Risk</h3>
    <p>
      Coinwush may use an index price or mark price for the purposes of
      calculating unrealized profit/loss, margin requirements, and liquidation
      triggers. This price is derived from external price feeds and may differ
      from the last traded price on the Platform. Manipulation or disruption of
      external price feeds may adversely affect your positions.
    </p>

    <h2>4. PLATFORM AND TECHNOLOGY RISKS</h2>

    <h3>4.1 System Downtime and Technical Failures</h3>
    <p>
      The Coinwush Platform depends on internet connectivity, cloud
      infrastructure, third-party data providers, and software systems.
      Technical failures, outages, bugs, server disruptions, or delays in data
      transmission may result in your inability to execute, modify, or close
      positions in a timely manner, potentially causing significant financial
      losses. Coinwush shall not be liable for losses arising from such
      technical events.
    </p>

    <h3>4.2 Cybersecurity Risks</h3>
    <p>
      Despite employing industry-standard security measures including encryption
      and multi-factor authentication, the Platform remains subject to risks of
      hacking, phishing, SIM swap attacks, malware, unauthorized account access,
      and other cyberattacks. Users are solely responsible for the security of
      their account credentials, devices, and two-factor authentication setups.
    </p>

    <h3>4.3 Third-Party Dependencies</h3>
    <p>
      The Platform may rely on third-party service providers for price feeds,
      payment processing, cloud services, and data infrastructure. Coinwush does
      not control these third parties and is not responsible for any losses,
      delays, or errors caused by their actions or failures.
    </p>

    <h3>4.4 Order Execution Risk</h3>
    <p>
      During periods of extreme market volatility or high platform load, orders
      may not be executed immediately or at the intended price. Stop-loss
      orders, take-profit orders, and liquidation orders are not guaranteed to
      be executed at the specified price
    </p>

    <h2>5. LEGAL AND REGULATORY RISKS</h2>

    <h3>5.1 Evolving Regulatory Framework</h3>
    <p>
      The legal and regulatory environment for cryptocurrency derivatives in
      India is uncertain and evolving. New laws, regulations, or directives from
      the Government of India, RBI, SEBI, FIU-India, or other authorities may be
      issued at any time that could restrict, limit, tax, or prohibit
      cryptocurrency derivatives trading activities. Any such regulatory change
      may adversely affect your ability to access or use the Platform.
    </p>

    <h3>5.2 Platform Regulatory Status</h3>
    <p>
      Questalious Private Limited is currently not registered with FIU-India as
      a reporting entity and the Platform is not regulated by SEBI, RBI, or any
      other Indian financial regulator. Accordingly, users of the Platform are
      not protected by any statutory investor protection, compensation scheme,
      or government guarantee. This may change in the future as regulations
      evolve.
    </p>

    <h3>5.3 Tax Obligations</h3>
    <p>
      Users are solely responsible for understanding and complying with all
      applicable tax obligations in India, including but not limited to income
      tax on trading gains and any applicable TDS provisions under the Income
      Tax Act, 1961. Nothing on the Platform constitutes tax advice. Users are
      strongly advised to consult a qualified tax professional.
    </p>

    <h3>5.4 Jurisdiction Restriction</h3>
    <p>
      The Platform is currently available to users in India only. Users
      accessing the Platform from outside India do so in violation of our Terms
      of Use and assume all legal and regulatory risks associated with doing so.
      Coinwush reserves the right to restrict or terminate access for users in
      any jurisdiction at any time.
    </p>

    <h3>5.5 Sanctions and Prohibited Activities</h3>
    <p>
      Users found to be engaging in prohibited activities, including money
      laundering, terrorism financing, market manipulation, or any other illegal
      activity, will have their accounts suspended and may be reported to
      relevant authorities. Coinwush shall not be liable for any resulting
      losses.
    </p>

    <h2>7. FRAUD AND SCAM AWARENESS</h2>

    <ul>
      <li>
        <strong>Phishing &amp; Impersonation:</strong> Fraudulent websites,
        emails, social media accounts, or applications may impersonate Coinwush
        to steal your account credentials or funds. Always verify that you are
        accessing the official Coinwush application.
      </li>

      <li>
        <strong>Unofficial Channels:</strong> Coinwush will never request your
        password, OTP, PIN, or any sensitive account information via email, SMS,
        social media, or phone calls. Any such request is fraudulent.
      </li>

      <li>
        <strong>Fake Investment Schemes:</strong> Beware of third parties
        claiming guaranteed returns or investment schemes linked to the Coinwush
        platform. Coinwush does not endorse any such schemes.
      </li>

      <li>
        <strong>User Vigilance:</strong> Users are responsible for promptly
        reporting any suspicious activity or unauthorized access by contacting
        support&#64;coinwush.com.
      </li>
    </ul>

    <h2>8. NO INVESTMENT ADVICE</h2>

    <p>
      Coinwush does not provide investment, financial, legal, or tax advice. Any
      information, data, price feeds, charts, signals, tools, calculators, or
      content available on the Platform is provided for informational purposes
      only and does not constitute a recommendation to buy, sell, or hold any
      position.
    </p>

    <p>
      Coinwush does not act as your broker, agent, advisor, or fiduciary. We do
      not monitor or evaluate whether your trading activity is appropriate for
      your financial situation, risk tolerance, or investment objectives. You
      are solely responsible for all trading decisions and their outcomes.
    </p>

    <h2>10. LIMITATION OF LIABILITY</h2>

    <p>
      To the fullest extent permitted by applicable law, Questalious Private
      Limited, its directors, officers, employees, agents, and affiliates shall
      not be liable for any direct, indirect, incidental, special,
      consequential, or punitive losses or damages arising from:
    </p>

    <ul>
      <li>Your use of, or inability to use, the Platform;</li>
      <li>Any trading losses, liquidations, or margin calls;</li>
      <li>Technical failures, system outages, or cybersecurity incidents;</li>
      <li>Actions or failures of third-party service providers;</li>
      <li>Regulatory changes or actions by governmental authorities;</li>
      <li>Inaccuracy or unavailability of price feeds or market data;</li>
      <li>Force majeure events as described in Section 11.</li>
    </ul>

    <p>
      This limitation of liability does not exclude or restrict any rights you
      may have under applicable Indian law that cannot be excluded or restricted
      by contract.
    </p>

    <h2>11. FORCE MAJEURE</h2>

    <p>
      Coinwush shall not be held liable for any failure or delay in the
      performance of its obligations resulting from events beyond its reasonable
      control, including but not limited to acts of God, natural disasters,
      floods, fires, earthquakes, pandemics, wars, civil unrest, government
      orders, regulatory actions, internet or telecommunications failures, power
      outages, or cyberattacks. In such events, Coinwush may suspend or restrict
      Platform services without prior notice and without liability for resulting
      losses.
    </p>

    <h2>12. AMENDMENTS</h2>

    <p>
      Questalious Private Limited reserves the right to amend, update, or
      replace this Risk Disclosure Statement at any time. The revised Disclosure
      will be published on the Platform with an updated effective date. Your
      continued use of the Platform after any such amendment constitutes your
      acceptance of the revised Disclosure.
    </p>

    <h2>13. CONTACT INFORMATION</h2>

    <p>
      For queries, concerns, or to report suspicious activity, please contact us
      at:
    </p>

    <p>
      <strong>Company:</strong> Questalious Private Limited<br />
      <strong>Platform:</strong> Coinwush<br />
      <strong>Email:</strong> support&#64;coinwush.com<br />
      <strong>CIN:</strong> U63119UP2025PTC222413
    </p>

    <h2>ACKNOWLEDGMENT</h2>

    <p>By accessing or using the Coinwush Platform, you confirm that:</p>

    <ul>
      <li>
        You have read and fully understood this Risk Disclosure Statement in its
        entirety;
      </li>
      <li>
        You understand that cryptocurrency derivatives trading involves a high
        degree of risk and may not be suitable for you;
      </li>
      <li>
        You are voluntarily choosing to trade on the Platform and accept full
        responsibility for all trading decisions and their financial
        consequences;
      </li>
      <li>
        You have not relied on any representation, warranty, or advice from
        Coinwush or its employees in making your decision to trade;
      </li>
      <li>
        You accept that Questalious Private Limited shall not be liable for any
        losses arising from your use of the Platform.
      </li>
    </ul>

    <!-- Final Risk Warning -->
    <div class="highlight-box danger">
      <h3>IMPORTANT DISCLAIMER:</h3>
      <p>
        Cryptocurrency derivatives products are unregulated and carry a high
        level of risk. There may be no regulatory recourse for any loss from
        such transactions. Digital assets are not legal tender and are not
        recognized or backed by the Reserve Bank of India or any other
        regulatory authority. Trading in cryptocurrency derivatives is
        speculative and you should only trade with funds you can afford to lose
        entirely.
      </p>
    </div>

    <!-- <div class="footer-note">
      <p>
        Questalious Private Limited | CIN: U63119UP2025PTC222413<br />
        155/101, Gali No. 7, Hari Nagar, Kankerkhera, Meerut – 250001, Uttar
        Pradesh, India<br />
        Website: https://coinwush.com | Support: support&#64;coinwush.com
      </p>
    </div> -->
  </div>
</div>
